/* eslint-disable indent */
import * as React from 'react';
import { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  InputAdornment
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage } from 'react-intl';
import { useMessages } from 'modules/intl';
import { postRequest } from 'utils/helper';
import { OauthPopupLoginAndRedirect } from 'modules/oauth/oauthPopupLoginAndRedirect';
import { encryptDataWithAES } from 'utils/hmac-auth';
import { useUIContext } from 'modules/common/hooks/useUIContext';
import { ConnectorInfo } from 'types/types';
import { DialogHeaderContext } from './DialogHeader';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2.5)
  },
  box: {
    display: 'flex',
    justifyContent: 'center'
  },
  image: {
    height: theme.spacing(12.5),
    width: theme.spacing(18.75)
  },
  textField: {
    marginBottom: theme.spacing(3.75)
  }
}));

type VantagepointInstallDialogPageProps = {
  info: ConnectorInfo;
  open: boolean;
  handleInstallDialogClose: () => void;
  setIsSignInAlertOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const messages = [
  'intermediateOauthDialog.inputError',
  'vantagepointInstallDialog.hostnameLabel',
  'vantagepointInstallDialog.hostnamePlaceholder',
  'vantagepointInstallDialog.hostnameHelper',
  'vantagepointInstallDialog.databaseLabel',
  'vantagepointInstallDialog.databaseHelper',
  'vantagepointInstallDialog.consumerKeyLabel',
  'vantagepointInstallDialog.consumerKeyHelper',
  'vantagepointInstallDialog.consumerSecretLabel',
  'vantagepointInstallDialog.consumerSecretHelper',
  'vantagepointInstallDialog.installationError'
];

const FIELDS = [
  'hostname',
  'database',
  'consumerKey',
  'consumerSecret'
] as const;

export const VantagepointInstallDialogPage: React.FC<VantagepointInstallDialogPageProps> = ({
  info,
  open,
  handleInstallDialogClose,
  setIsSignInAlertOpen
}: VantagepointInstallDialogPageProps) => {
  const [input, setInput] = useState({
    hostname: '',
    database: '',
    consumerKey: '',
    consumerSecret: ''
  });
  const [inputError, setInputError] = useState({
    hostnameError: '',
    databaseError: '',
    consumerKeyError: '',
    consumerSecretError: ''
  });
  const [connected, setConnected] = useState(false);
  const { data } = useUIContext();
  const classes = useStyles();
  const errorMessage = useMessages(messages);

  const requiredInputError = errorMessage['intermediateOauthDialog.inputError'];

  const handleError = useCallback(
    (value: string, textfieldName: string) => {
      setInputError((prev: any) => ({
        ...prev,
        [textfieldName]: value.trim() === '' ? requiredInputError : ''
      }));
    },
    [requiredInputError]
  );

  const handleText = useCallback(
    (event: any, textfieldName: string, textfieldErrorName: string) => {
      const value = event.target.value;
      setInput((prev: any) => ({
        ...prev,
        [textfieldName]: value
      }));
      handleError(value, textfieldErrorName);
    },
    [handleError]
  );

  const handleApiCall = useCallback(async () => {
    try {
      const token = {
        client_id: input.consumerKey,
        client_secret: input.consumerSecret
      };
      const jsonToken = JSON.stringify(token);
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        environment: 'production',
        isPsaProduct: info.isPsaProduct,
        database: input.database,
        hostName: input.hostname,
        token: encryptDataWithAES(jsonToken, data.installationID)
      };
      const res = await postRequest(
        '/integration-settings-api/install-connector',
        body,
        false
      );
      if (res.data) {
        setConnected(true);
        setIsSignInAlertOpen(true);
        OauthPopupLoginAndRedirect(res.data, info.name);
      }
    } catch (error: any) {
      alert(
        `${errorMessage['vantagepointInstallDialog.installationError']}: ${error.message}`
      );
    }
  }, [
    input.hostname,
    input.database,
    input.consumerKey,
    input.consumerSecret,
    info.shortName,
    info.companyKey,
    info.isPsaProduct,
    info.name,
    data.installationID,
    setIsSignInAlertOpen,
    errorMessage
  ]);

  const validateAndConnect = (event: any) => {
    event.preventDefault();
    const value = event.target.value;

    let hasError = false;

    FIELDS.forEach((field) => {
      if (input[field].trim() === '') {
        handleError(value, `${field}Error`);
        hasError = true;
      }
    });

    if (!hasError) {
      handleApiCall();
    }
  };

  /* eslint-disable indent */
  const helperText = useCallback(
    (textfieldId: string) => {
      switch (textfieldId) {
        case 'hostname':
          return errorMessage['vantagepointInstallDialog.hostnameHelper'];
        case 'database':
          return errorMessage['vantagepointInstallDialog.databaseHelper'];
        case 'consumerKey':
          return errorMessage['vantagepointInstallDialog.consumerKeyHelper'];
        case 'consumerSecret':
          return errorMessage['vantagepointInstallDialog.consumerSecretHelper'];
      }
    },
    [errorMessage]
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (connected) {
      timeoutId = setTimeout(() => {
        handleInstallDialogClose();
        setConnected(false);
      }, 2000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [connected, handleInstallDialogClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleInstallDialogClose}
        data-testid="render-all-contents"
      >
        <DialogTitle
          className={classes.header}
          data-testid="render-dialog-title"
        >
          <FormattedMessage id={'intermediateOauthDialog.header'} />
        </DialogTitle>
        <DialogContent>
          <DialogHeaderContext shortName={info.shortName} />
          <Box className={classes.box} data-testid="render-box">
            <div>
              <br />
              <TextField
                id="hostname"
                variant="outlined"
                required
                fullWidth
                value={input.hostname}
                onChange={(event) =>
                  handleText(event, 'hostname', 'hostnameError')
                }
                error={Boolean(inputError.hostnameError)}
                label={errorMessage['vantagepointInstallDialog.hostnameLabel']}
                type={'text'}
                placeholder={
                  errorMessage['vantagepointInstallDialog.hostnamePlaceholder']
                }
                helperText={
                  inputError.hostnameError
                    ? inputError.hostnameError
                    : helperText('hostname')
                }
                className={classes.textField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">https://</InputAdornment>
                  )
                }}
                inputProps={{ 'data-testid': 'hostnameInput' }}
              />
              <br />
              <TextField
                id="database"
                variant="outlined"
                required
                fullWidth
                value={input.database}
                onChange={(event) =>
                  handleText(event, 'database', 'databaseError')
                }
                error={Boolean(inputError.databaseError)}
                label={errorMessage['vantagepointInstallDialog.databaseLabel']}
                type={'text'}
                InputLabelProps={{
                  shrink: true
                }}
                helperText={
                  inputError.databaseError
                    ? inputError.databaseError
                    : helperText('database')
                }
                className={classes.textField}
                inputProps={{ 'data-testid': 'databaseInput' }}
              />
              <br />
              <TextField
                id="consumerKey"
                variant="outlined"
                required
                fullWidth
                value={input.consumerKey}
                onChange={(event) =>
                  handleText(event, 'consumerKey', 'consumerKeyError')
                }
                error={Boolean(inputError.consumerKeyError)}
                label={
                  errorMessage['vantagepointInstallDialog.consumerKeyLabel']
                }
                type={'text'}
                InputLabelProps={{
                  shrink: true
                }}
                helperText={
                  inputError.consumerKeyError
                    ? inputError.consumerKeyError
                    : helperText('consumerKey')
                }
                className={classes.textField}
                inputProps={{ 'data-testid': 'consumerKeyInput' }}
              />
              <br />
              <TextField
                id="consumerSecret"
                variant="outlined"
                required
                fullWidth
                value={input.consumerSecret}
                onChange={(event) =>
                  handleText(event, 'consumerSecret', 'consumerSecretError')
                }
                error={Boolean(inputError.consumerSecretError)}
                label={
                  errorMessage['vantagepointInstallDialog.consumerSecretLabel']
                }
                type={'password'}
                InputLabelProps={{
                  shrink: true
                }}
                helperText={
                  inputError.consumerSecretError
                    ? inputError.consumerSecretError
                    : helperText('consumerSecret')
                }
                className={classes.textField}
                inputProps={{ 'data-testid': 'consumerSecretInput' }}
              />
              <br />
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleInstallDialogClose}
            color="grey"
            autoFocus
            variant="contained"
            data-testid="cancel-button"
          >
            <FormattedMessage id={'connectorSetting.cancel'} />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={validateAndConnect}
            data-testid="connect-button"
          >
            <FormattedMessage id={'intermediateOauthDialog.connectButton'} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
